import { Settings as SettingsIcon, List as ListIcon, Download, Send, Terminal as TerminalIcon, ShieldCheck, LogOut } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import type { CollectClient } from '../lib/api';
import { ConnectionStatus } from './ConnectionStatus';
import { onInstallAvailable, triggerInstall, isStandalone } from '../lib/pwa';
import { isTauri } from '../lib/tauri';
import type { PassSession } from '../lib/session';

export type Route = 'capsules' | 'send' | 'terminal' | 'settings';

export function AppShell({
  client,
  route,
  onRouteChange,
  refreshKey,
  pass,
  onSignOut,
  children,
}: {
  client: CollectClient;
  /** Highlighted nav item; null on screens outside the nav (login, not found). */
  route: Route | null;
  onRouteChange: (r: Route) => void;
  refreshKey?: number;
  /** Ordinal Pass session, when signed in that way. */
  pass?: PassSession | null;
  onSignOut?: () => void;
  children: ReactNode;
}) {
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const off = onInstallAvailable(setCanInstall);
    return off;
  }, []);

  const handleInstall = async () => {
    const outcome = await triggerInstall();
    if (outcome === 'accepted') {
      setInstalled(true);
      setCanInstall(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-emerald-500/15 backdrop-blur-md sticky top-0 z-10" style={{ background: 'rgba(5,5,5,0.6)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-6">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center heartbeat" style={{
              background: 'linear-gradient(135deg, #10B981, #064E3B)',
            }}>
              <svg viewBox="0 0 32 32" width="20" height="20" fill="none" stroke="#F0FDF4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 16 L10 16 L13 10 L16 22 L19 8 L22 20 L25 16 L28 16" />
              </svg>
            </div>
            <div>
              <div className="display text-xl leading-none">Collect</div>
              <div className="label" style={{ fontSize: '9px', marginTop: '2px' }}>capsules</div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex items-center gap-1 md:ml-6 order-last md:order-none w-full md:w-auto overflow-x-auto">
            <NavButton active={route === 'capsules'} onClick={() => onRouteChange('capsules')} icon={<ListIcon size={14} />}>Capsules</NavButton>
            <NavButton active={route === 'send'} onClick={() => onRouteChange('send')} icon={<Send size={14} />}>Send</NavButton>
            {isTauri() && (
              <NavButton active={route === 'terminal'} onClick={() => onRouteChange('terminal')} icon={<TerminalIcon size={14} />}>Terminal</NavButton>
            )}
            <NavButton active={route === 'settings'} onClick={() => onRouteChange('settings')} icon={<SettingsIcon size={14} />}>Settings</NavButton>
          </nav>

          <div className="flex-1" />

          {/* Install PWA */}
          {canInstall && !installed && (
            <button
              onClick={handleInstall}
              className="btn btn-ghost text-xs"
              style={{ padding: '6px 12px', fontSize: '12px' }}
              title="Install Collect as an app on your device"
            >
              <Download size={12} />
              Install
            </button>
          )}

          {/* Connection */}
          <ConnectionStatus client={client} refreshKey={refreshKey} />

          {/* Ordinal Pass session */}
          {pass && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-mono" style={{ color: '#34D399' }} title={`Signed in with Ordinal Pass · ${pass.sub}`}>
              <ShieldCheck size={12} />
              {pass.workspaceSlug || 'Ordinal Pass'}
            </span>
          )}
          {onSignOut && (
            <button onClick={onSignOut} className="btn btn-ghost text-xs whitespace-nowrap" style={{ padding: '6px 12px', fontSize: '12px' }} title="Sign out">
              <LogOut size={12} />
              Sign out
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-6 py-10 w-full">
        {children}
      </main>

      <footer className="border-t border-emerald-500/10 px-6 py-4 text-xs" style={{ color: 'rgba(240,253,244,0.4)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>Collect — capsules are SHA-256 hashed and gzip-compressed; ML-KEM-768 encryption only via the CLI (--encrypt)</span>
          <span className="font-mono">qodoonect.com</span>
        </div>
      </footer>
    </div>
  );
}

function NavButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: ReactNode; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all"
      style={{
        color: active ? '#10B981' : 'rgba(240,253,244,0.65)',
        background: active ? 'rgba(16,185,129,0.08)' : 'transparent',
        border: active ? '1px solid rgba(16,185,129,0.25)' : '1px solid transparent',
      }}
    >
      {icon}
      {children}
    </button>
  );
}
